import {yupResolver} from '@hookform/resolvers/yup';
import {skipToken} from '@reduxjs/toolkit/query';
import {round} from 'lodash';
import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {FormProvider, useFieldArray, useForm, useWatch} from 'react-hook-form';
import {ScrollView, Text, View} from 'react-native';
import {Card, Divider} from 'react-native-paper';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import {useSelector} from 'react-redux';
import * as yup from 'yup';
import {buildLogoUrl, ShowToast} from '../Common/common';
import BTButton from '../Components/BTButton';
import BTCheckbox from '../Components/BTCheckbox';
import BTComboBox from '../Components/BTComboBox';
import {Col, Row} from '../Components/BTGrid';
import {TextInput} from '../Components/BTTextInput';
import {useGetModesQuery} from '../redux/services/amodeApi';
import {useGetFOECheckinAdvByRoomsQuery} from '../redux/services/foecheckinadvApi';
import {
  useGetFOECheckinsQuery,
  useGetFOEOccRoomsQuery,
} from '../redux/services/foecheckinApi';
import {
  useGetFOECheckinByIdQuery,
  useGetFOECheckinByIdRoomsQuery,
  useInsertFOECheckoutMutation,
} from '../redux/services/foecheckoutApi';
import {useGetFOEPostingsByRoomQuery} from '../redux/services/foepostingsApi';
import {useGetFOSettingByIdQuery} from '../redux/services/foasettingApi';
import {RootState} from '../redux/store';
import styles from '../styles/FOStyles';
import CheckoutBill from './Print/CheckoutBill';
import { findHeadByTariff } from '../Common/headUtils';
import { sortPostingsByDateRoom } from '../Common/postingOrder';
import { useGetFOMHeadsQuery } from '../redux/services/fomheadApi';
import { useGetEmployeesQuery } from '../redux/services/emmemployeeApi';

import T from '../Components/T';
import { tp } from '../utils/translateText';
const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const validationSchema = yup.object().shape({
  co_mobile: yup.string().required('Mobile No is required'),
  co_name: yup.string().required('Guest Name is required'),
  // Required only when FO setting se_hkempcheckout = 1 (yup context)
  co_employee: yup
    .number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value,
    )
    .when('$hkEmpRequired', {
      is: true,
      then: schema =>
        schema.test(
          'hk-employee-required',
          'Housekeeping Employee is required for Check-out',
          value => Number(value) > 0,
        ),
      otherwise: schema => schema.optional(),
    }),
  co_tax1no: yup
    .string()
    .nullable()
    .optional()
    .test(
      'is-valid-gst',
      'Invalid GST Number',
      value => !value || value.trim() === '' || gstRegex.test(value.trim().toUpperCase()),
    ),
  co_posting: yup.array().of(
    yup.object().shape({
      ps_room: yup.mixed().required('Room is required'),
      ps_rate: yup
        .number()
        .typeError('Rate must be a number')
        .required('Rate is required'),
      ps_amt: yup
        .number()
        .typeError('Amount must be a number')
        .required('Amount is required'),
    }),
  ),
  co_settle: yup.array().of(
    yup.object().shape({
      cs_mode: yup
        .number()
        .typeError('Mode is required')
        .min(1, 'Please select payment mode')
        .required('Mode is required'),
      cs_amt: yup
        .number()
        .typeError('Amount must be a number')
        .required('Amount is required'),
    }),
  ),
});

const CheckOutForm = ({navigation, route}: UniversalProps) => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const cid = gdata.company;
  const {data: foSetting} = useGetFOSettingByIdQuery(cid);
  const {data: headData} = useGetFOMHeadsQuery(cid);
  const {data: employeeData} = useGetEmployeesQuery(cid);
  const employees = employeeData?.map((item: any) => ({
    id: item.em_id,
    label: `${item.em_name} (${item.em_code || ''})`.trim(),
  }));
  const [isSaving, setIsSaving] = useState(false);
  const [selCheckins, setSelCheckins] = useState<string | undefined>();
  const [selTrans, setSelTrans] = useState<number[]>([]);
  const [selAdvs, setSelAdvs] = useState<string[]>([]);
  const [selRooms, setSelRooms] = useState('');
  const [remarks, setRemarks] = useState();
  const [ota, setOta] = useState(false);
  const [setBal, setSetBal] = useState(0);

  const {data: modes} = useGetModesQuery(cid);
  const {data: occData} = useGetFOEOccRoomsQuery({cid: cid, coid: 0});
  const {data: checkinData} = useGetFOECheckinsQuery(cid);
  const isQuerySkip = !selCheckins || !selRooms;
  const {data: coData} = useGetFOECheckinByIdRoomsQuery(
    isQuerySkip
      ? skipToken
      : {
          id: selCheckins,
          rooms: selRooms,
        },
    {
      refetchOnFocus: true,
    },
  );
  const {data: postData = null} = useGetFOEPostingsByRoomQuery(
    selRooms ?? skipToken,
    {
      refetchOnFocus: true,
    },
  );
  const {data: advData = null} = useGetFOECheckinAdvByRoomsQuery(
    selCheckins ?? skipToken,
    {
      refetchOnFocus: true,
    },
  );
  const [insertCheckout] = useInsertFOECheckoutMutation();

  // Duplicated from ui/src/screens/pages/Transaction/FOCheckout/calculations.ts -
  // no shared package between ui/ and app/ (plan section 5), same duplication
  // pattern already used for redux/services/* between the two apps.
  const calculateHourlyAmount = (
    stayedHours: number,
    minHours: number,
    baseRent: number,
    interval: number,
    ratePerInterval: number,
  ): number => {
    const safeMinHours = minHours > 0 ? minHours : 0;
    const safeInterval = interval > 0 ? interval : 1;
    if (safeMinHours === 0) {
      return round(Math.ceil(stayedHours / safeInterval) * ratePerInterval, 2);
    }
    if (stayedHours <= safeMinHours) {
      return round(baseRent, 2);
    }
    const extraIntervals = Math.ceil(
      (stayedHours - safeMinHours) / safeInterval,
    );
    return round(baseRent + extraIntervals * ratePerInterval, 2);
  };

  const calculateRefreshmentAmount = (
    stayedHours: number,
    refreshHours: number,
    refreshRate: number,
    minHours: number,
    baseRent: number,
    interval: number,
    hourlyRate: number,
    interval24?: number | null,
    rate24?: number | null,
  ): number => {
    if (stayedHours <= refreshHours) {
      return refreshRate;
    }
    const safeInterval = interval > 0 ? interval : 1;
    const safeMinHours = minHours > 0 ? minHours : (refreshHours > 0 ? refreshHours : 1);
    const effectiveBaseRent = baseRent > 0 ? baseRent : (hourlyRate > 0 ? safeMinHours * hourlyRate : refreshRate);

    // Past 24h with a Rate after 24 Hrs configured, Day 2+ bills at it (same as Hourly), so
    // the total is the day-block sum. Without one the flat formula below is unchanged.
    if ((Number(rate24) || 0) > 0 && stayedHours > Math.max(24, safeMinHours)) {
      return round(
        calculateRefreshmentAmountWithBlocks(
          stayedHours, refreshHours, refreshRate, minHours, baseRent, interval, hourlyRate, interval24, rate24,
        ).reduce((sum, block) => sum + block.amount, 0),
        2,
      );
    }

    if (stayedHours <= safeMinHours) {
      return effectiveBaseRent;
    }
    const extraIntervals = Math.ceil((stayedHours - safeMinHours) / safeInterval);
    return effectiveBaseRent + extraIntervals * hourlyRate;
  };

  // Duplicated from ui/src/screens/pages/Transaction/FOCheckout/calculations.ts -
  // splits an Hourly stay into rolling-24hr day blocks. Splitting is unconditional once
  // the stay exceeds 24h; Day 2+ falls back to Day 1's own interval/rate when no escalated
  // rate is configured (rate24<=0), so every multi-day Hourly stay itemizes by day.
  const calculateHourlyAmountWithBlocks = (
    stayedHours: number,
    minHours: number,
    baseRent: number,
    interval: number,
    ratePerInterval: number,
    interval24?: number | null,
    rate24?: number | null,
  ): {dayIndex: number; hoursInBlock: number; amount: number}[] => {
    // Day 1's boundary must absorb minHours when it exceeds 24h, otherwise baseRent's
    // covered hours (24..minHours) would be double-billed by an incorrectly-early Day 2 split.
    const splitBoundary = Math.max(24, Number(minHours) || 0);
    const day1Hours = Math.min(stayedHours, splitBoundary);
    const blocks: {dayIndex: number; hoursInBlock: number; amount: number}[] = [
      {
        dayIndex: 0,
        hoursInBlock: day1Hours,
        amount: calculateHourlyAmount(day1Hours, minHours, baseRent, interval, ratePerInterval),
      },
    ];
    const safeRate24 = Number(rate24) || 0;
    const effectiveRate = safeRate24 > 0 ? safeRate24 : ratePerInterval;
    // Blank Interval-after-24h uses Day 1's interval, not 1h (a per-4h rate billed per hour
    // would over-charge up to 24x) - mirrors ui FOCheckout/calculations.ts.
    const day1Interval = interval > 0 ? interval : 1;
    const effectiveInterval = safeRate24 > 0 && Number(interval24) > 0 ? Number(interval24) : day1Interval;
    let remaining = stayedHours - day1Hours;
    let dayIndex = 1;
    while (remaining > 0) {
      const blockHours = Math.min(remaining, 24);
      blocks.push({
        dayIndex,
        hoursInBlock: blockHours,
        amount: round(Math.ceil(blockHours / effectiveInterval) * effectiveRate, 2),
      });
      remaining -= blockHours;
      dayIndex += 1;
    }
    return blocks;
  };

  // Duplicated from ui/src/screens/pages/Transaction/FOCheckout/calculations.ts -
  // block-returning sibling of calculateRefreshmentAmount, so a Refreshment stay produces one
  // row per rolling-24hr day like Hourly does. Needed because the posting head (and therefore
  // the GST band) is chosen per day now, and a single flat row has no per-day amount to band
  // against. Within the threshold the stay is well under 24h, so it stays one block. Past it,
  // the whole stay reverts to the Hourly tariff (the locked "discard the refreshment rate on
  // overflow" decision) and is split by calculateHourlyAmountWithBlocks. Day 2+ bills at the
  // Rate/Interval after 24 Hrs when configured, exactly like Hourly.
  //
  // The block sum equals the old flat total when `interval` divides both day 1's billable
  // remainder (24 - minHours) and each later block's hours - always true for interval=1, the
  // common config. Otherwise each day boundary rounds up by at most one interval (an interval
  // dividing 24 is not sufficient). Hourly already behaves this way. Pinned by
  // CheckOutForm.perDayBand.test.tsx.
  const calculateRefreshmentAmountWithBlocks = (
    stayedHours: number,
    refreshHours: number,
    refreshRate: number,
    minHours: number,
    baseRent: number,
    interval: number,
    hourlyRate: number,
    interval24?: number | null,
    rate24?: number | null,
  ): {dayIndex: number; hoursInBlock: number; amount: number}[] => {
    // Threshold test kept identical to calculateRefreshmentAmount's above.
    if (stayedHours <= refreshHours) {
      return [{dayIndex: 0, hoursInBlock: stayedHours, amount: round(refreshRate, 2)}];
    }
    const safeMinHours = minHours > 0 ? minHours : (refreshHours > 0 ? refreshHours : 1);
    const effectiveBaseRent = baseRent > 0 ? baseRent : (hourlyRate > 0 ? safeMinHours * hourlyRate : refreshRate);
    return calculateHourlyAmountWithBlocks(
      stayedHours,
      safeMinHours,
      effectiveBaseRent,
      interval,
      hourlyRate,
      interval24,
      rate24,
    );
  };

  // Duplicated from ui/src/screens/pages/Transaction/FOCheckout/calculations.ts.
  const calculateHourlyExtraBedAmount = (
    stayedHours: number,
    extraBedInterval: number,
    hourlyEachRate: number,
    extraBedCount: number,
  ): number => {
    const safeExtraBedCount = Number(extraBedCount) > 0 ? Number(extraBedCount) : 0;
    const safeRate = Number(hourlyEachRate) || 0;
    if (safeExtraBedCount <= 0 || safeRate <= 0) {
      return 0;
    }
    const safeInterval = Number(extraBedInterval) > 0 ? Number(extraBedInterval) : 1;
    return round(Math.ceil(stayedHours / safeInterval) * safeRate * safeExtraBedCount, 2);
  };

  // Duplicated from ui/src/screens/pages/Transaction/FOCheckout/calculations.ts -
  // splitting is unconditional once the stay exceeds 24h; Day 2+ falls back to Day 1's
  // own extraBedInterval/hourlyEachRate when no escalated rate is configured, mirroring
  // calculateHourlyAmountWithBlocks' own fallback above.
  const calculateHourlyExtraBedAmountWithBlocks = (
    stayedHours: number,
    extraBedInterval: number,
    hourlyEachRate: number,
    extraBedCount: number,
    extraBedInterval24?: number | null,
    hourlyEachRate24?: number | null,
    minHours?: number,
  ): {dayIndex: number; hoursInBlock: number; amount: number}[] => {
    // Day 1's boundary must absorb minHours when it exceeds 24h, mirroring
    // calculateHourlyAmountWithBlocks' own fix - otherwise the room-tariff's baseRent-
    // covered hours would be double-billed for extra beds by an incorrectly-early Day 2
    // split. minHours defaults to 0 (Math.max(24, 0) === 24) so any other caller is
    // unaffected.
    const splitBoundary = Math.max(24, Number(minHours) || 0);
    const day1Hours = Math.min(stayedHours, splitBoundary);
    const blocks: {dayIndex: number; hoursInBlock: number; amount: number}[] = [
      {
        dayIndex: 0,
        hoursInBlock: day1Hours,
        amount: calculateHourlyExtraBedAmount(day1Hours, extraBedInterval, hourlyEachRate, extraBedCount),
      },
    ];
    const safeRate24 = Number(hourlyEachRate24) || 0;
    const effectiveRate = safeRate24 > 0 ? safeRate24 : hourlyEachRate;
    const day1Interval = Number(extraBedInterval) > 0 ? Number(extraBedInterval) : 1;
    const effectiveInterval =
      safeRate24 > 0 && Number(extraBedInterval24) > 0 ? Number(extraBedInterval24) : day1Interval;
    const safeExtraBedCount = Number(extraBedCount) > 0 ? Number(extraBedCount) : 0;
    let remaining = stayedHours - day1Hours;
    let dayIndex = 1;
    while (remaining > 0) {
      const blockHours = Math.min(remaining, 24);
      blocks.push({
        dayIndex,
        hoursInBlock: blockHours,
        amount: round(Math.ceil(blockHours / effectiveInterval) * effectiveRate * safeExtraBedCount, 2),
      });
      remaining -= blockHours;
      dayIndex += 1;
    }
    return blocks;
  };

  const checkins = checkinData?.map(item => ({
    label: `${item.ci_no} - ${item.ab_name}`,
    id: item.ci_id,
  }));

  const initialValues = {
    co_id: 0,
    co_company: cid,
    co_date:
      moment(gdata.today).format('YYYY/MM/DD') + moment().format(' HH:mm:ss'),
    co_no: '',
    co_mobile: '',
    co_name: '',
    co_addr1: '',
    co_addr2: '',
    co_city: 0,
    co_state: gdata.defstate,
    co_country: gdata.defcountry,
    co_pincode: '',
    co_arrival: moment().add(-1, 'days').format('YYYY/MM/DD HH:mm:ss'),
    co_days: 1,
    co_hours: 0,
    co_depature:
      moment(gdata.today).format('YYYY/MM/DD') + moment().format(' HH:mm:ss'),
    co_email: '',
    co_cname: '',
    co_caddr: '',
    co_dept: '',
    co_tax1no: '',
    co_tax2no: '',
    co_tax3no: '',
    co_employee: 0,
    co_flag: 1,
    co_uid: gdata.user,
    co_cdt: moment().format('YYYY/MM/DD HH:mm:ss'),
    co_muid: gdata.user,
    co_mdt: moment().format('YYYY/MM/DD HH:mm:ss'),
    co_details: [],
    co_settle: [],
    co_posting: [],
    co_advs: [],
    co_totcr: 0,
    co_totdr: 0,
    co_totdisc: 0,
    co_bal: 0,
    co_commamt: 0,
    co_setamt: 0,
    co_setbal: 0,
    co_edflag: 0,
    co_split: 0,
    co_type: false,
    co_custcopy: false,
  };

  const methods = useForm<any>({
    defaultValues: initialValues,
    resolver: (values, context, options) =>
      yupResolver(validationSchema)(
        values,
        {...context, hkEmpRequired: foSetting?.se_hkempcheckout === 1},
        options,
      ),
  });

  const {
    fields: postings,
    append: appendPosting,
    remove: removePosting,
  } = useFieldArray({control: methods.control, name: 'co_posting'});
  const {
    fields: settlements,
    append: appendSettle,
    remove: removeSettle,
  } = useFieldArray({control: methods.control, name: 'co_settle'});

  useEffect(() => {
    if (route.params?.checkinId) {
      setSelCheckins(String(route.params.checkinId));
      methods.setValue('ca_checkin', Number(route.params.checkinId));
    }
    if (route.params?.room || route.params?.rooms) {
      const roomParam = route.params?.rooms || route.params?.room;
      const roomIds = Array.isArray(roomParam)
        ? roomParam.map(Number)
        : String(roomParam).split(',').map(Number);
      setSelRooms(roomIds.join(','));
      const matchingRooms = occData?.filter((v: any) =>
        roomIds.includes(Number(v.rm_id)),
      );
      if (matchingRooms && matchingRooms.length > 0) {
        const checkinIds = Array.from(
          new Set(matchingRooms.map((v: any) => v.ci_id.toString())),
        );
        setSelCheckins(checkinIds.join(','));
        methods.setValue('ca_checkin', matchingRooms[0].ci_id);
      }
    }
  }, [route.params?.room, route.params?.rooms, route.params?.checkinId, occData, checkins]);

  useEffect(() => {
    if (coData) {
      const checkinData: any = coData;
      methods.setValue('co_mobile', checkinData.ab_mobile);
      methods.setValue('co_name', checkinData.ab_name);
      methods.setValue('co_addr1', checkinData.ab_addr1);
      methods.setValue('co_addr2', checkinData.ab_addr2);
      methods.setValue('co_city', checkinData.ab_city);
      methods.setValue('co_pincode', checkinData.ab_pincode);
      methods.setValue('co_state', checkinData.ab_state);
      methods.setValue('co_country', checkinData.ab_country);
      methods.setValue(
        'co_arrival',
        moment(checkinData.ci_arrvial).format('YYYY/MM/DD HH:mm'),
      );
      methods.setValue(
        'co_depature',
        moment(gdata.today).format('YYYY/MM/DD') + moment().format(' HH:mm:ss'),
      );
      //methods.setValue('co_days', checkinData.ci_days);
      methods.setValue('co_cname', checkinData.ab_cname);
      methods.setValue('co_caddr', checkinData.ab_caddr);
      methods.setValue('co_dept', checkinData.ab_dept);
      methods.setValue('co_tax1no', checkinData.ab_tax1no);
      methods.setValue('co_tax2no', checkinData.ab_tax2no);
      methods.setValue('co_tax3no', checkinData.ab_tax3no);
      setRemarks(checkinData.ci_remarks);
      methods.setValue('ab_id', checkinData.ab_id);

      methods.setValue('ot_id', checkinData.ot_id);
      methods.setValue('ot_no', checkinData.ot_no);
      methods.setValue('ot_date', checkinData.ot_date);
      methods.setValue('ot_days', checkinData.ot_days);
      methods.setValue('ot_rooms', checkinData.ot_rooms);
      methods.setValue('ot_tariff', checkinData.ot_tariff);
      methods.setValue('ot_eb', checkinData.ot_eb);
      methods.setValue('ot_tax', checkinData.ot_tax);
      methods.setValue('ot_gross', checkinData.ot_gross);
      methods.setValue('ot_net', checkinData.ot_net);
      methods.setValue('ot_comm', checkinData.ot_comm);
      methods.setValue('ot_commamt', checkinData.ot_commamt);
      methods.setValue('ot_ctax', checkinData.ot_ctax);
      methods.setValue('ot_ctaxamt', checkinData.ot_ctaxamt);
      methods.setValue('ot_commision', checkinData.ot_commision);
      methods.setValue('ot_tcs', checkinData.ot_tcs);
      methods.setValue('ot_tcsamt', checkinData.ot_tcsamt);
      methods.setValue('ot_tds', checkinData.ot_tds);
      methods.setValue('ot_tdsamt', checkinData.ot_tdsamt);

      if (!isNaN(checkinData.ot_commision))
        methods.setValue(
          'co_commamt',
          (
            Number(checkinData.ot_commision) +
            Number(checkinData.ot_tcsamt) +
            Number(checkinData.ot_tdsamt)
          ).toFixed(2),
        );
      methods.setValue('co_edflag', 0);
      methods.setValue('co_custcopy', false);
      methods.setValue('co_posting1', checkinData.co_posting1);
      methods.setValue('co_pax', checkinData.co_pax);

      //calcTotal();
    }
  }, [coData]);

  useEffect(() => {
    // Recompute Hourly/Refreshment room-tariff postings against the actual stayed
    // duration (arrival -> now, since mobile checkout has no departure-time picker
    // and always settles as of "now" - see plan section 5). Daily/Monthly rooms are
    // untouched. Hourly stays past 24h split into multiple day-block rows (plan
    // section 4); Refreshment and the Hourly Extra-Bed charge stay single-row.
    const arrival = coData?.ci_arrvial ? moment(coData.ci_arrvial) : null;
    const departure = moment();
    const diffHours =
      arrival && arrival.isValid()
        ? Math.max(0, Math.ceil(departure.diff(arrival, 'hours', true)))
        : 0;

    let combinedRows: any[] = postData ? [...postData] : [];

    if (arrival && arrival.isValid() && postData) {
      const roomTariffByRoom: Record<string, any[]> = {};
      const extraBedByRoom: Record<string, any[]> = {};
      // Plan charge (hd_type 2) and extra-child (hd_type 4) never day-split, but they are
      // part of the day's total room charge, so they feed the per-day GST band below and get
      // re-banded themselves.
      const planByRoom: Record<string, any[]> = {};
      const extraChildByRoom: Record<string, any[]> = {};
      postData.forEach((v: any) => {
        const key = String(v.ps_curroom);
        if (v.hd_type === 1) {
          (roomTariffByRoom[key] = roomTariffByRoom[key] || []).push(v);
        } else if (v.hd_type === 3) {
          (extraBedByRoom[key] = extraBedByRoom[key] || []).push(v);
        } else if (v.hd_type === 2) {
          (planByRoom[key] = planByRoom[key] || []).push(v);
        } else if (v.hd_type === 4) {
          (extraChildByRoom[key] = extraChildByRoom[key] || []).push(v);
        }
      });

      const otherRows = postData.filter((v: any) => v.hd_type !== 1 && v.hd_type !== 3);
      const roomTariffFinalRows: any[] = [];
      const extraBedFinalRows: any[] = [];

      const byDate = (a: any, b: any) => moment(a.ps_date).diff(moment(b.ps_date));
      const sumRates = (rows?: any[]) =>
        (rows || []).reduce((acc, r) => acc + (Number(r.ps_rate) || 0), 0);

      // Single source of truth for the rd_* (live tariff master) -> cd_* (check-in snapshot)
      // fallbacks, shared by the per-day band precompute and the billing loops below so the
      // band can never disagree with the amount actually charged.
      const deriveTariffInputs = (rep: any) => {
        const minHours =
          rep.rd_minhours != null
            ? Number(rep.rd_minhours)
            : (rep.cd_minhours != null ? Number(rep.cd_minhours) : 1);
        const rawInterval = Number(rep.rd_interval ?? rep.cd_interval);
        return {
          minHours,
          baseRent: Number(rep.rd_baserent ?? rep.cd_baserent) || 0,
          interval: rawInterval > 0 ? rawInterval : 1,
          hourlyRate: Number(rep.rd_hourlyrate ?? rep.cd_unitrate) || 0,
          interval24: Number(rep.rd_interval24 ?? rep.cd_interval24) || 0,
          rate24: Number(rep.rd_hourlyrate24 ?? rep.cd_hourlyrate24) || 0,
          refreshHours: Number(rep.rd_refreshhours ?? rep.cd_refreshhours) || 0,
          refreshRate: Number(rep.rd_refreshrate ?? rep.cd_refreshrate) || 0,
        };
      };

      const deriveExtraBedInputs = (rep: any) => {
        const minHours =
          rep.rd_minhours != null
            ? Number(rep.rd_minhours)
            : (rep.cd_minhours != null ? Number(rep.cd_minhours) : 1);
        return {
          minHours,
          hourlyEachRate: Number(rep.rd_hourlyeachrg ?? rep.cd_hourlyeachrg) || 0,
          interval: Number(rep.rd_extrabedinterval ?? rep.cd_extrabedinterval) || 0,
          interval24: Number(rep.rd_extrabedinterval24 ?? rep.cd_extrabedinterval24) || 0,
          rate24: Number(rep.rd_hourlyeachrg24 ?? rep.cd_hourlyeachrg24) || 0,
          refreshHours: Number(rep.rd_refreshhours ?? rep.cd_refreshhours) || 0,
          // rf_occ NULL means the tariff-master join failed; base occupancy is unknown, so
          // treating every adult as an extra bed would massively over-bill.
          occKnown: rep.rf_occ !== null && rep.rf_occ !== undefined,
          extraBedCount: Math.max(0, (Number(rep.cd_adult) || 0) - (Number(rep.rf_occ) || 0)),
        };
      };

      // ---------------------------------------------------------------------------------
      // Per-day GST band base.
      //
      // fo_mhead is both the posting-head master and the GST band table (hd_mintariff..
      // hd_maxtariff + hd_tax1per/2per/3per), so a row's head IS its tax rate. Check-in picks
      // that head once, from cd_rate + cd_plrate + cd_earate + cd_ecrate - correct for Daily
      // (a per-night amount) but wrong for Hourly/Refreshment, where cd_rate is the
      // WHOLE-STAY total. A 3-day hourly stay at 1800/day bands as if the room cost 5400 a
      // night, pushing a 12% room into the 18% slab.
      //
      // Band each row by the charge for ITS day instead: the day's room-tariff block + that
      // day's extra-bed block + the plan and extra-child charges (both per-day charges, so
      // they count toward every day).
      //
      // Computed before the loops because the extra-bed blocks are produced in the second
      // loop, and the first loop already needs the base.
      // ---------------------------------------------------------------------------------
      const dayBaseByRoom: Record<string, number[]> = {};
      Object.keys(roomTariffByRoom).forEach(key => {
        const rep = [...roomTariffByRoom[key]].sort(byDate)[0];
        const rentalType = rep?.cd_rentaltype;
        if (rentalType !== 'H' && rentalType !== 'R') return;

        const t = deriveTariffInputs(rep);
        const roomBlocks =
          rentalType === 'H'
            ? calculateHourlyAmountWithBlocks(
                diffHours >= t.minHours ? diffHours : t.minHours,
                t.minHours,
                t.baseRent,
                t.interval,
                t.hourlyRate,
                t.interval24,
                t.rate24,
              )
            : calculateRefreshmentAmountWithBlocks(
                diffHours,
                t.refreshHours,
                t.refreshRate,
                t.minHours,
                t.baseRent,
                t.interval,
                t.hourlyRate,
                t.interval24,
                t.rate24,
              );

        // ebBlocks carries an hours-accumulated charge belonging to a specific day;
        // ebFlatPerDay carries the flat rf_eachrg*count charge, which applies every day.
        let ebBlocks: {dayIndex: number; hoursInBlock: number; amount: number}[] = [];
        let ebFlatPerDay = 0;
        const ebRows = extraBedByRoom[key];
        if (ebRows && ebRows.length > 0) {
          const ebRep = [...ebRows].sort(byDate)[0];
          const eb = deriveExtraBedInputs(ebRep);
          const eligible =
            eb.hourlyEachRate > 0 &&
            eb.occKnown &&
            (rentalType === 'H' || (eb.refreshHours > 0 && diffHours > eb.refreshHours));
          if (eligible) {
            const ebFinalHours = diffHours >= eb.minHours ? diffHours : eb.minHours;
            if (rentalType === 'H' && eb.extraBedCount > 0) {
              // Argument list matches the billing loop below exactly, minHours included,
              // so the band and the charge cannot disagree.
              ebBlocks = calculateHourlyExtraBedAmountWithBlocks(
                ebFinalHours,
                eb.interval,
                eb.hourlyEachRate,
                eb.extraBedCount,
                eb.interval24,
                eb.rate24,
                eb.minHours,
              );
            } else {
              // 'R' never day-splits its extra bed, so the whole charge sits on day 0.
              ebBlocks = [
                {
                  dayIndex: 0,
                  hoursInBlock: ebFinalHours,
                  amount: calculateHourlyExtraBedAmount(
                    ebFinalHours,
                    eb.interval,
                    eb.hourlyEachRate,
                    eb.extraBedCount,
                  ),
                },
              ];
            }
          } else {
            // Flat rf_eachrg*count is a per-DAY charge, so it counts toward every day's
            // base. Read it off the still-pending row when there is one - summing the group
            // would double-count a charge already partly settled.
            ebFlatPerDay = Number((ebRows.find(r => Number(r.ps_id) === 0) ?? ebRep).ps_rate) || 0;
          }
        }

        const planAmt = sumRates(planByRoom[key]);
        const extraChildAmt = sumRates(extraChildByRoom[key]);

        dayBaseByRoom[key] = roomBlocks.map((block, d) =>
          round(
            block.amount + (ebBlocks[d]?.amount ?? 0) + ebFlatPerDay + planAmt + extraChildAmt,
            2,
          ),
        );
      });

      // Room-tariff and extra-bed block counts match for 'H', but not for 'R' (its extra bed
      // stays one row while the room tariff day-splits), and plan/extra-child groups may not
      // exist at all - so always index through this.
      const dayBaseFor = (key: string, dayIndex: number): number | null => {
        const bases = dayBaseByRoom[key];
        if (!bases || bases.length === 0) return null;
        return bases[dayIndex] ?? bases[bases.length - 1];
      };

      /**
       * Re-bands one row: picks the fo_mhead row whose band contains `base` within the row's
       * own hd_type, and returns a copy carrying that head and its tax percentages. The tax
       * AMOUNTS are then derived from these percentages by the recompute pass at the end of
       * this effect, so nothing else needs to change.
       *
       * Returns the row untouched when there is no base, no covering band, or the row is a
       * house guest (vw_fopostings pins those to the lowest hd_type=1 head regardless of
       * amount).
       */
      const bandRow = (row: any, base: number | null, hdType: number) => {
        if (base === null) return row;
        if (String(row.cd_hg ?? '0') === '1') return row;
        const matched = findHeadByTariff(base, hdType, headData);
        if (!matched) return row;
        return {
          ...row,
          ps_head: Number(matched.hd_id),
          ps_tax1per: Number(matched.hd_tax1per) || 0,
          ps_tax2per: Number(matched.hd_tax2per) || 0,
          ps_tax3per: Number(matched.hd_tax3per) || 0,
        };
      };

      Object.keys(roomTariffByRoom).forEach(key => {
        const rows = [...roomTariffByRoom[key]].sort(byDate);
        const representative = rows[0];
        const rentalType = representative.cd_rentaltype;
        const postedRows = rows.filter(r => Number(r.ps_id) > 0);
        const pendingTemplate = rows.find(r => Number(r.ps_id) === 0);

        if (rentalType !== 'H' && rentalType !== 'R') {
          roomTariffFinalRows.push(...rows);
          return;
        }

        const t = deriveTariffInputs(representative);

        // Already-settled rows are left exactly as billed - re-banding them would
        // retroactively restate a bill that has already been issued.
        roomTariffFinalRows.push(...postedRows);

        if (rentalType === 'H') {
          const finalHours = diffHours >= t.minHours ? diffHours : t.minHours;
          const blocks = calculateHourlyAmountWithBlocks(
            finalHours,
            t.minHours,
            t.baseRent,
            t.interval,
            t.hourlyRate,
            t.interval24,
            t.rate24,
          );
          const settledCount = postedRows.length;
          const effectiveRate24 = t.rate24 > 0 ? t.rate24 : t.hourlyRate;
          const effectiveInterval24 = t.rate24 > 0 && t.interval24 > 0 ? t.interval24 : t.interval;

          if (settledCount <= blocks.length && pendingTemplate) {
            const outstandingBlocks = blocks.slice(settledCount);
            outstandingBlocks.forEach(block => {
              const label =
                block.dayIndex === 0
                  ? (block.hoursInBlock > t.minHours
                      ? `Extra Hours: ${Math.max(0, block.hoursInBlock - t.minHours)}h`
                      : 'Room Tariff')
                  : `Day ${block.dayIndex + 1}${block.hoursInBlock < 24 ? ' Partial' : ''} (${block.hoursInBlock}h @ ₹${effectiveRate24}/${effectiveInterval24}h)`;
              roomTariffFinalRows.push(
                bandRow(
                  {
                    ...pendingTemplate,
                    ps_id: 0,
                    ps_rate: block.amount,
                    ps_discamt: 0,
                    ps_date: arrival.clone().add(block.dayIndex, 'days').format('YYYY/MM/DD HH:mm:ss'),
                    ps_narr: label,
                  },
                  dayBaseFor(key, block.dayIndex),
                  1,
                ),
              );
            });
          }
        } else {
          // Refreshment: flat within the threshold, else the whole stay reverts to the
          // Hourly tariff (locked decision - not refreshment + hourly overage). Now
          // day-split like 'H' once an overflowed stay runs past 24h, so each day can be
          // banded by its own charge. Day 2+ bills at the Rate after 24 Hrs when set, like H.
          const blocks = calculateRefreshmentAmountWithBlocks(
            diffHours,
            t.refreshHours,
            t.refreshRate,
            t.minHours,
            t.baseRent,
            t.interval,
            t.hourlyRate,
            t.interval24,
            t.rate24,
          );
          const overflowed = t.refreshHours > 0 && diffHours > t.refreshHours;
          const safeMinHours = t.minHours > 0 ? t.minHours : (t.refreshHours > 0 ? t.refreshHours : 1);
          const effectiveBaseRent =
            t.baseRent > 0 ? t.baseRent : (t.hourlyRate > 0 ? safeMinHours * t.hourlyRate : t.refreshRate);

          // Count-based reconciliation assumes each posted row equals its corresponding
          // block. That holds normally (the night audit skips 'R'), but NOT after a partial
          // checkout, where FOECheckoutController INSERTs pending rows for real and they
          // come back with ps_id > 0 carrying whatever amount was diced off. Detect that and
          // fall back to the original amount-based single row, which self-corrects.
          const sumPosted = sumRates(postedRows);
          const expectedPosted = round(
            blocks.slice(0, postedRows.length).reduce((acc, b) => acc + b.amount, 0),
            2,
          );

          if (sumPosted > 0 && round(sumPosted, 2) !== expectedPosted) {
            const total = calculateRefreshmentAmount(
              diffHours,
              t.refreshHours,
              t.refreshRate,
              t.minHours,
              t.baseRent,
              t.interval,
              t.hourlyRate,
              t.interval24,
              t.rate24,
            );
            if (pendingTemplate) {
              roomTariffFinalRows.push(
                bandRow(
                  {
                    ...pendingTemplate,
                    ps_rate: Math.max(0, total - sumPosted),
                    ps_date: arrival.format('YYYY/MM/DD HH:mm:ss'),
                  },
                  dayBaseFor(key, 0),
                  1,
                ),
              );
            }
          } else if (postedRows.length <= blocks.length && pendingTemplate) {
            const outstandingBlocks = blocks.slice(postedRows.length);
            outstandingBlocks.forEach(block => {
              const extraHours = Math.max(0, Math.min(diffHours, block.hoursInBlock) - safeMinHours);
              const extraAmount = Math.ceil(extraHours / t.interval) * t.hourlyRate;
              const label =
                block.dayIndex === 0
                  ? (overflowed
                      ? `Hourly Base: ${effectiveBaseRent} (${safeMinHours}h)${extraHours > 0 ? ` + Extra ${extraHours}h: ${extraAmount}` : ''}`
                      : `Refreshment Stay (${diffHours}h)`)
                  : `Day ${block.dayIndex + 1}${block.hoursInBlock < 24 ? ' Partial' : ''} (${block.hoursInBlock}h @ ₹${t.rate24 > 0 ? t.rate24 : t.hourlyRate}/${t.rate24 > 0 && t.interval24 > 0 ? t.interval24 : t.interval}h)`;
              roomTariffFinalRows.push(
                bandRow(
                  {
                    ...pendingTemplate,
                    ps_id: 0,
                    ps_rate: block.amount,
                    ps_discamt: 0,
                    ps_date: arrival.clone().add(block.dayIndex, 'days').format('YYYY/MM/DD HH:mm:ss'),
                    ps_narr: label,
                  },
                  dayBaseFor(key, block.dayIndex),
                  1,
                ),
              );
            });
          }
        }
      });

      Object.keys(extraBedByRoom).forEach(key => {
        const rows = [...extraBedByRoom[key]].sort(byDate);
        const representative = rows[0];
        const extraBedRentalType = representative.cd_rentaltype;
        const eb = deriveExtraBedInputs(representative);

        // Extra bed only escalates for Hourly rooms, or for Refreshment rooms once the
        // stay has overflowed past its threshold into full Hourly billing - mirrors the
        // room-tariff 'R' branch's own overflow switch above. Within the Refreshment
        // threshold, the flat check-in-time cd_earate row stays untouched (else branch).
        const extraBedOverflowed =
          extraBedRentalType === 'R' && eb.refreshHours > 0 && diffHours > eb.refreshHours;
        const extraBedEligible = extraBedRentalType === 'H' || extraBedOverflowed;
        const bandable = extraBedRentalType === 'H' || extraBedRentalType === 'R';

        if (extraBedEligible && eb.hourlyEachRate > 0 && eb.occKnown) {
          // Floor billed hours to the room's minimum-hours block, same as the room-tariff
          // branch's finalHours above - otherwise a short stay bills the room at its
          // minimum-stay rate while under-billing the extra bed against fewer raw hours.
          const extraBedFinalHours = diffHours >= eb.minHours ? diffHours : eb.minHours;
          const postedRows = rows.filter(r => Number(r.ps_id) > 0);
          const pendingTemplate = rows.find(r => Number(r.ps_id) === 0);

          const effectiveEBRate24 = eb.rate24 > 0 ? eb.rate24 : eb.hourlyEachRate;
          const effectiveEBInterval24 = eb.rate24 > 0 && eb.interval24 > 0 ? eb.interval24 : (eb.interval || 1);
          const daySplitEligible = extraBedRentalType === 'H' && eb.extraBedCount > 0;

          extraBedFinalRows.push(...postedRows);

          if (daySplitEligible) {
            const blocks = calculateHourlyExtraBedAmountWithBlocks(
              extraBedFinalHours,
              eb.interval,
              eb.hourlyEachRate,
              eb.extraBedCount,
              eb.interval24,
              eb.rate24,
              eb.minHours,
            );
            const settledCount = postedRows.length;
            if (settledCount <= blocks.length && pendingTemplate) {
              const outstandingBlocks = blocks.slice(settledCount);
              outstandingBlocks.forEach(block => {
                const label =
                  block.dayIndex === 0
                    ? ''
                    : `Day ${block.dayIndex + 1}${block.hoursInBlock < 24 ? ' Partial' : ''} Extra Bed (${block.hoursInBlock}h @ ₹${effectiveEBRate24}/${effectiveEBInterval24}h)`;
                extraBedFinalRows.push(
                  bandRow(
                    {
                      ...pendingTemplate,
                      ps_id: 0,
                      ps_rate: block.amount,
                      ps_discamt: 0,
                      ps_date: arrival.clone().add(block.dayIndex, 'days').format('YYYY/MM/DD HH:mm:ss'),
                      ps_narr: label,
                    },
                    dayBaseFor(key, block.dayIndex),
                    3,
                  ),
                );
              });
            }
          } else if (pendingTemplate) {
            const amount = calculateHourlyExtraBedAmount(
              extraBedFinalHours,
              eb.interval,
              eb.hourlyEachRate,
              eb.extraBedCount,
            );
            const sumPosted = sumRates(postedRows);
            extraBedFinalRows.push(
              bandRow(
                {
                  ...pendingTemplate,
                  ps_rate: Math.max(0, amount - sumPosted),
                  ps_date: arrival.format('YYYY/MM/DD HH:mm:ss'),
                  ps_narr: '',
                },
                dayBaseFor(key, 0),
                3,
              ),
            );
          }
        } else {
          // Not escalating - the flat check-in-time cd_earate row's AMOUNT stands, but it is
          // still part of the day's room charge, so it still belongs in the correct band.
          rows.forEach(r => {
            extraBedFinalRows.push(
              bandable && Number(r.ps_id) === 0
                ? bandRow(
                    {
                      ...r,
                      ps_date: arrival.format('YYYY/MM/DD HH:mm:ss'),
                    },
                    dayBaseFor(key, 0),
                    3,
                  )
                : r,
            );
          });
        }
      });

      // Plan charge and extra-child rows ride along in otherRows; re-band the pending ones
      // against day 0's total room charge, within their own head type.
      const bandedOtherRows = otherRows.map((v: any) => {
        if (Number(v.ps_id) !== 0) return v;
        if (v.cd_rentaltype !== 'H' && v.cd_rentaltype !== 'R') return v;
        if (v.hd_type !== 2 && v.hd_type !== 4) return v;
        return bandRow(
          {
            ...v,
            ps_date: arrival.format('YYYY/MM/DD HH:mm:ss'),
          },
          dayBaseFor(String(v.ps_curroom), 0),
          Number(v.hd_type),
        );
      });

      // The three groups are rebuilt separately above; put them back in bill order (date,
      // then room number) so Day 2+ rows sit with their day, same as web checkout.
      combinedRows = sortPostingsByDateRoom([
        ...bandedOtherRows,
        ...roomTariffFinalRows,
        ...extraBedFinalRows,
      ]);
    }

    const updatedPostData = combinedRows.map(v => {
      const ps_rate = Number(v?.ps_rate || 0);
      const discAmt = Number(v?.ps_discamt || 0);
      const tax1per = Number(v?.ps_tax1per || 0);
      const tax2per = Number(v?.ps_tax2per || 0);
      const tax3per = Number(v?.ps_tax3per || 0);
      const taxable = Math.max(0, ps_rate - discAmt);
      const ps_tax1amt = (taxable * tax1per) / 100;
      const ps_tax2amt = (taxable * tax2per) / 100;
      const ps_tax3amt = (taxable * tax3per) / 100;
      return {
        ...v,
        ps_rate,
        ps_tax1amt,
        ps_tax2amt,
        ps_tax3amt,
        ps_amt: ps_rate - discAmt + ps_tax1amt + ps_tax2amt + ps_tax3amt,
      };
    });

    methods.setValue('co_settle', []);
    methods.setValue('co_posting', updatedPostData);
    methods.setValue('co_advs', advData);

    const hasDurationBilled = postData?.some(
      (v: any) => v.hd_type === 1 && (v.cd_rentaltype === 'H' || v.cd_rentaltype === 'R'),
    );
    if (hasDurationBilled) {
      // co_days is a true day count always now (mirrors the web fix) - co_hours (UI-only,
      // never sent to the backend, same reasoning as web) carries the hour count.
      methods.setValue('co_days', 0);
      methods.setValue('co_hours', diffHours);
    } else {
      let days = 0;
      let froom = 0;
      postData?.map((v, i) => {
        if (froom === 0) froom = Number(v.ps_curroom);
        // Unused-night (early departure) rows are not days the guest stayed
        if (v.hd_type === 1 && Number(v.ps_curroom) === froom && !(v as any).ps_earlydep) days++;
      });
      const arr = moment(methods.getValues('co_arrival'));
      const dep = moment(methods.getValues('co_depature'));
      let calcDays = 0;
      if (arr.isValid() && dep.isValid()) {
        calcDays = dep.clone().startOf('day').diff(arr.clone().startOf('day'), 'days');
      }
      const initialDays = Math.max(calcDays, Number(coData?.ci_days) || 0, days);
      methods.setValue('co_days', initialDays > 0 ? initialDays : 1);
    }

    let totCr = 0,
      totDr = 0,
      totDisc = 0;
    if (updatedPostData && Array.isArray(updatedPostData)) {
      for (const v of updatedPostData) {
        totDr +=
          Number(v?.ps_rate || 0) +
          Number(v?.ps_tax1amt || 0) +
          Number(v?.ps_tax2amt || 0) +
          Number(v?.ps_tax3amt || 0);
        totDisc += Number(v?.ps_discamt || 0);
      }
    }

    if (advData && Array.isArray(advData)) {
      for (const v of advData) {
        totCr += Number(v?.ca_amt || 0);
      }
    }
    let comm = !isNaN(methods.getValues('co_commamt'))
      ? Number(methods.getValues('co_commamt'))
      : 0;
    setOta(comm > 0);

    const calculatedBal = round(totDr - totCr - totDisc - comm, comm > 0 ? 2 : 0);

    methods.setValue('co_totcr', Number(totCr).toFixed(2));
    methods.setValue('co_totdr', Number(totDr).toFixed(2));
    methods.setValue('co_totdisc', Number(totDisc).toFixed(2));
    methods.setValue('co_bal', calculatedBal.toFixed(2));

    const defaultMode = Number(gdata.defmode) > 0 ? Number(gdata.defmode) : (modes?.[0]?.md_id ? Number(modes[0].md_id) : 1);
    if (calculatedBal > 0) {
      methods.setValue('co_settle', [
        {
          cs_id: 0,
          cs_srid: 1,
          cs_amt: calculatedBal,
          cs_mode: defaultMode,
          cs_refno: '',
          cs_refdt: moment().format('YYYY/MM/DD HH:mm:ss'),
          cs_refbank: 0,
          cs_company: 0,
          cs_room: 0,
          cs_type: 0,
        },
      ]);
      setSetBal(0);
    } else {
      methods.setValue('co_settle', []);
      setSetBal(calculatedBal);
    }
  }, [postData, advData, coData]);

  // `settlements` (useFieldArray's `fields`) only reflects structural changes
  // (append/remove), not live keystroke edits to an already-added row's
  // amount - and the previous dependency, methods.getValues(), is a new
  // object every call so it never actually re-triggered this effect on
  // meaningful changes, only on unrelated parent re-renders. useWatch tracks
  // the live field values, so both the trigger and the sum come from the
  // same source now.
  const watchedSettle = useWatch({control: methods.control, name: 'co_settle'});

  useEffect(() => {
    const totalSettleAmt =
      (watchedSettle || []).reduce(
        (sum: number, v: any) => sum + Number(v?.cs_amt || 0),
        0,
      ) || 0;

    const newBal = round(Number(methods.getValues('co_bal') || 0) - totalSettleAmt, 2);
    setSetBal(newBal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedSettle]);

  const onSubmit = async (values: any) => {
    if (isSaving) return;
    if (Math.abs(setBal) > 0.01) {
      ShowToast('Enter Correct Settlement?', 'error');
      return;
    }

    if (foSetting?.se_manualreceipt === 1 && Array.isArray(values.co_settle)) {
      for (const settleRow of values.co_settle) {
        if (Number(settleRow.cs_amt) > 0 && !String(settleRow.cs_refno || '').trim()) {
          ShowToast('Manual Receipt No / Reference is required for settlement', 'error');
          return;
        }
      }
    }

    setIsSaving(true);

    const isAdvExcluded = (v: any) => {
      const key = `${v.ca_type}-${v.ca_id}-${v.ca_checkin || 0}-${v.ca_no || ''}`;
      const legacyKey = `${v.ca_type}-${v.ca_id}`;
      return selAdvs.includes(key) || selAdvs.includes(legacyKey);
    };

    const advs = methods.getValues('co_advs') || [];
    const postingsList = methods.getValues('co_posting') || [];

    let k = 1;
    let dets = advs
      .filter((v: any) => !isAdvExcluded(v))
      .map((val: any) => {
        return {
          cd_id: 0,
          cd_srid: k++,
          cd_checkin: val.ca_checkin,
          cd_room: 0,
          cd_advtype: val.ca_type,
          cd_advid: val.ca_id,
          cd_psid: 0,
        };
      });

    dets = [
      ...dets,
      ...postingsList
        .filter((v: any) => !selTrans.includes(v.ps_id))
        .map((val: any, i: number) => {
          return {
            cd_id: 0,
            cd_srid: k++,
            cd_checkin: val.ps_checkin,
            cd_room: val.ps_room,
            cd_advtype: '',
            cd_advid: 0,
            cd_psid: val.ps_id,
            cd_pssrid: i,
          };
        }),
    ];
    values.co_details = dets;
    values.co_advs = (values.co_advs || advs).filter(
      (v: any) => !isAdvExcluded(v),
    );
    values.co_posting = (values.co_posting || postingsList).filter(
      (v: any) => !selTrans.includes(v.ps_id),
    );
    values.co_rooms = selRooms
      ? selRooms
          .split(',')
          .filter(Boolean)
          .map(Number)
          .filter(id => !isNaN(id) && id > 0)
      : [];

    try {
      const dat: any = await insertCheckout(values).unwrap();

      const dat1 = {
        cm_code: gdata.company,
        cm_name: gdata.companyname,
        cm_addr1: gdata.addr1 + ', ',
        cm_addr2: gdata.addr2,
        cm_addr3: gdata.city + ' - ' + gdata.pincode,
        cm_phone: 'Phone : ' + gdata.phone,
        cm_taxno1: gdata.tax1no,
        cm_taxno2: gdata.tax2no,
        cm_taxno3: gdata.tax3no,
        cm_logo: buildLogoUrl(gdata.company, gdata.logo),
      };

      try {
        await CheckoutBill({rdat: dat, dat1: dat1});
      } catch (printErr) {
        console.warn('Bill preview error:', printErr);
      }

      ShowToast('Checkout Successful');

      methods.reset(initialValues);
      navigation.navigate('StatusScreen');
    } catch (e: any) {
      console.error(e);
      // The backend deliberately returns specific reasons for a rejected
      // checkout (e.g. "Please Close All POS Orders", "Empty Checkout") -
      // previously only a generic "Checkout Failed" reached the user, making
      // it hard for staff to know why.
      ShowToast(e?.data?.message || e?.data?.error || e?.message || 'Checkout Failed', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const onError = (errors: any) => {
    console.warn('Checkout form validation errors:', errors);
    const getFirstMessage = (errObj: any): string | null => {
      if (!errObj) return null;
      if (typeof errObj.message === 'string' && errObj.message) return errObj.message;
      for (const key of Object.keys(errObj)) {
        const msg = getFirstMessage(errObj[key]);
        if (msg) return msg;
      }
      return null;
    };
    const message = getFirstMessage(errors) || 'Please fill all required fields correctly';
    ShowToast(message, 'error');
  };

  return (
    <View style={{ flex: 1, backgroundColor: '#f5f5f5' }}>
      <ScrollView
        contentContainerStyle={{
          padding: 8,
          paddingBottom: 80,
          flexGrow: 1,
          backgroundColor: '#f5f5f5',
        }}
        keyboardShouldPersistTaps="handled">
      <View style={styles.header}>
        <Text style={styles.title}><T>Check-out</T></Text>
      </View>
      <FormProvider {...methods}>
        <Card style={styles.cardfill}>
          <Card.Content>
            <Row>
              <Col>
                <TextInput
                  name="co_mobile"
                  label="Mobile No"
                  placeholder={tp("Enter Mobile No")}
                  keyboardType="phone-pad"
                />
              </Col>
              <Col>
                <TextInput
                  name="co_name"
                  label="Guest Name"
                  placeholder={tp("Enter Guest Name")}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextInput
                  name="co_addr1"
                  label="Address Line 1"
                  placeholder={tp("Enter Address Line 1")}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextInput
                  name="co_addr2"
                  label="Address Line 2"
                  placeholder={tp("Enter Address Line 2")}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextInput
                  name="co_city"
                  label="City"
                  placeholder={tp("Enter City")}
                />
              </Col>
              <Col>
                <TextInput
                  name="co_pincode"
                  label="Pincode"
                  placeholder={tp("Enter Pincode")}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextInput
                  name="co_tax1no"
                  label="GSTIN"
                  placeholder={tp("Enter GSTIN")}
                  autoCapitalize="characters"
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <BTComboBox
                  name="co_employee"
                  label={
                    foSetting?.se_hkempcheckout === 1
                      ? 'HK Employee *'
                      : 'HK Employee'
                  }
                  data={employees}
                  placeholder={tp("Select HK Employee")}
                />
              </Col>
            </Row>

            <Divider style={{marginVertical: 8}} />
            <Row>
              <Col>
                <Text style={styles.description}><T>Total Charges</T></Text>
              </Col>
              <Col>
                <Text style={styles.amount}>
                  {Number(methods.getValues('co_totdr')).toFixed(2)}
                </Text>
              </Col>
            </Row>
            <Row>
              <Col>
                <Text style={styles.description}><T>Total Advance</T></Text>
              </Col>
              <Col>
                <Text style={styles.amount}>
                  {Number(methods.getValues('co_totcr')).toFixed(2)}
                </Text>
              </Col>
            </Row>
            <Row>
              <Col>
                <Text style={styles.description}><T>Total Discount</T></Text>
              </Col>
              <Col>
                <Text style={styles.amount}>
                  {Number(methods.getValues('co_totdisc')).toFixed(2)}
                </Text>
              </Col>
            </Row>
            <Row>
              <Col>
                <Text style={styles.description}><T>Commission</T></Text>
              </Col>
              <Col>
                <Text style={styles.amount}>
                  {Number(methods.getValues('co_commamt')).toFixed(2)}
                </Text>
              </Col>
            </Row>
            <Row>
              <Col>
                <Text style={styles.description}><T>Balance</T></Text>
              </Col>
              <Col>
                <Text style={styles.amount}>
                  {Number(methods.getValues('co_bal')).toFixed(2)}
                </Text>
              </Col>
            </Row>
            <Row>
              <Col>
                <Text style={styles.description}><T>Settlement Balance</T></Text>
              </Col>
              <Col>
                <Text style={styles.amount}>{Number(setBal).toFixed(2)}</Text>
              </Col>
            </Row>
            <Divider style={{marginVertical: 8}} />
            <Text style={{fontWeight: 'bold', marginBottom: 4}}>
              <T>Settlement</T>
            </Text>
            {settlements?.map((item, index) => (
              <View key={item.id || `settle-${index}`} style={{marginBottom: 8}}>
                <Row>
                  <Col>
                    <BTComboBox
                      name={`co_settle.${index}.cs_mode`}
                      label="Mode"
                      data={modes?.map(mode => ({
                        id: mode.md_id,
                        label: mode.md_name,
                      }))}
                      placeholder={tp("Select Mode")}
                    />
                  </Col>
                  <Col>
                    <TextInput
                      name={`co_settle.${index}.cs_amt`}
                      label="Amount"
                      keyboardType="numeric"
                      textAlign="right"
                    />
                  </Col>
                  {index > 0 && (
                    <FontAwesome
                      name="trash"
                      size={20}
                      color="#d11a2a"
                      onPress={() => removeSettle(index)}
                    />
                  )}
                </Row>
                <Row>
                  <Col>
                    <TextInput
                      name={`co_settle.${index}.cs_refno`}
                      label={
                        foSetting?.se_manualreceipt === 1
                          ? 'Receipt / Ref No *'
                          : 'Receipt / Ref No'
                      }
                      placeholder={tp("Receipt / Ref No")}
                    />
                  </Col>
                </Row>
              </View>
            ))}
            <Row style={styles.addRowBtn}>
              <FontAwesome
                name="plus-circle"
                size={26}
                color="#146C94"
                onPress={() =>
                  appendSettle({
                    cs_id: 0,
                    cs_srid: settlements.length + 1,
                    cs_amt: setBal,
                    cs_mode: gdata.defmode,
                    cs_refno: '',
                    cs_refdt: moment().format('YYYY/MM/DD HH:mm:ss'),
                    cs_refbank: 0,
                    cs_company: 0,
                    cs_room: 0,
                    cs_type: 0,
                  })
                }
              />
            </Row>

            <Divider />
            <Row>
              <Col>
                <BTCheckbox label="" name="co_type" defaultValue={true} />
              </Col>
              <Col>
                <BTButton
                  title="Save"
                  onPress={methods.handleSubmit(onSubmit, onError)}
                  onLongPress={() => {}}
                  color="#146C94"
                  disabled={isSaving}
                />
              </Col>
              <Col>
                <BTButton
                  title="Cancel"
                  onPress={() => {
                    methods.reset(initialValues);
                    navigation.navigate('StatusScreen');
                  }}
                  onLongPress={() => {}}
                  color="#146C94"
                />
              </Col>
            </Row>

            <Text style={styles.description}><T>Advances</T></Text>
            <Divider style={{marginVertical: 8}} />
            {advData?.map((item: any, index: Number) => (
              <View key={`post-${index}`} style={styles.detailFragment}>
                <Row>
                  <Col>
                    <Text style={styles.description}>
                      {moment(item.ca_date).format('DD-MM')}
                    </Text>
                  </Col>
                  <Col>
                    <Text style={styles.amount}>{item.ca_no}</Text>
                  </Col>
                  <Col>
                    <Text style={styles.amount}>
                      {Number(item.ca_amt).toFixed(0)}
                    </Text>
                  </Col>
                </Row>
              </View>
            ))}
            <Text style={styles.description}><T>Charges</T></Text>
            <Divider style={{marginVertical: 8}} />
            {postings?.map((item: any, index) => (
              <View key={`post-${index}`} style={styles.detailFragment}>
                <Row>
                  <Col>
                    <Text style={styles.description}>
                      {moment(item.ps_date).format('DD-MM')}
                    </Text>
                  </Col>
                  <Col>
                    <Text style={styles.amount}>
                      {Number(item.ps_rate).toFixed(0)}
                    </Text>
                  </Col>
                  <Col>
                    <Text style={styles.amount}>
                      {Number(item.ps_tax1amt).toFixed(2)}
                    </Text>
                  </Col>
                  <Col>
                    <Text style={styles.amount}>
                      {Number(item.ps_tax2amt).toFixed(2)}
                    </Text>
                  </Col>
                  <Col>
                    <Text style={styles.amount}>
                      {Number(item.ps_amt).toFixed(0)}
                    </Text>
                  </Col>
                </Row>
              </View>
            ))}
          </Card.Content>
        </Card>
      </FormProvider>
      </ScrollView>
    </View>
  );
};

export default CheckOutForm;
